import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Catcher, LinkComponent, TableItemDeleteColumn, TableItemToolbar, columnDeleted, columnToolbar } from '../../';
import { ICardListItem, IDataTypeMain, INameObjectN, ISelect, NotificationType } from '../../../interfaces';
import { useCardsList, useProfile, useProfileIsRight, useUiIsLoading } from '../../../hooks';
import { addNotification, deleteCardWithSubscribers, setUiForcedUpdateTime } from '../../../middleware';
import { cleanColumnsDataSource } from '../../../instruments';
import { tF, tI } from '../../../translate';
import '../../../customStyle/table.less';
import Styles from '../../styles/m_viewStyles.less';

type IDataType = IDataTypeMain &
    Omit<
        ICardListItem,
        'id' | 'cardSets' | 'companies' | 'deletedAt' | 'plmnList' | 'printedId' | 'shortId' | 'subscribers'
    > & {
        companies?: ReactNode;
        deleted?: ReactNode;
        nameLink?: ReactNode;
        printedId?: ReactNode;
        setList?: ReactNode;
    };

export const CardTableList: React.FC = observer(() => {
    const isLord = useProfileIsRight('LORD');
    const isLoading = useUiIsLoading();
    const cardList = useCardsList();
    const { isCompanyTypeIdMNOHost } = useProfile();

    const MNOHostColumn = [
        {
            title: tF('Printed ID'),
            dataIndex: 'printedId',
            // ToDo: 19.08.2021 - Printed ID must be the ICCID of the  subscribers allowed in the creation of the company and will be displayed here, all other ICCIDs and Printed IDs must be hidden.
        },
    ];
    const allColumn = [
        {
            title: tF('Name'),
            dataIndex: 'nameLink',
        },
        {
            title: tF('Printed ID'),
            dataIndex: 'printedId',
        },
        {
            title: tF('Card Sets'),
            dataIndex: 'setList',
        },
        {
            title: tF('Companies'),
            dataIndex: 'companies',
        },
    ];
    const tableColumns: ColumnsType<IDataType> = [
        ...(isCompanyTypeIdMNOHost ? MNOHostColumn : allColumn),
        columnDeleted(isLord),
        columnToolbar,
    ];

    const handleDelete = async (id: string): Promise<void> => {
        const resp = await deleteCardWithSubscribers(id);

        if (resp.isError) {
            addNotification({
                type: NotificationType.ERROR,
                message: 'An error has occurred while deleting the card',
                isUsersError: true,
            });
        }

        setUiForcedUpdateTime();
    };

    const tableData: IDataType[] = cardList.map((c: ICardListItem) => {
        const CardSetListLinks = c.cardSets?.map((s: ISelect) => {
            return <LinkComponent currentListComponent={'CardSetsList'} id={s.id} isLight name={s.name} key={s.id} />;
        });
        const printedId = c.printedId ? c.printedId : `# ${c.id}`;
        const CompaniesListLinks = c.companies?.map((co: INameObjectN, i: number) => (
            <LinkComponent currentListComponent={'CompaniesList'} id={co.id} name={co.name} key={`${co.id}-${i}`} />
        ));

        return {
            key: c.id,
            companies: CompaniesListLinks,
            deleted: <TableItemDeleteColumn deletedAt={c.deletedAt} />,
            nameLink: <LinkComponent currentListComponent={'CardList'} id={c.id} isLight name={c.name} />,
            printedId: <LinkComponent currentListComponent={'CardList'} id={c.id} isLight name={printedId} />,
            setList: CardSetListLinks,
            toolbar: (
                <TableItemToolbar
                    deletedAt={c.deletedAt}
                    deleteItem={isLord ? (): Promise<void> => handleDelete(c.id) : undefined}
                    id={c.id}
                    itemName={`"${printedId}" ${tI('card')}`}
                    popupMessage={
                        'Deleting this card will delete all IMSIs (subscribers) attached to it from the system. Do you want to proceed?'
                    }
                />
            ),
        };
    });

    return (
        <Catcher>
            <div className={Styles.tablePlace} data-test="CardListPlace">
                <Table
                    className={'table-theme'}
                    {...cleanColumnsDataSource<IDataType>(tableColumns, tableData)}
                    loading={isLoading}
                    pagination={false}
                    size={'middle'}
                />
            </div>
        </Catcher>
    );
});
